// useStagedUpload.ts
import { useCallback, useMemo } from "react";
import {
  useStagedUploadsStore,
  stagedUploadsActions,
  type StagedFile,
  type StagedFileInput,
  type StagedUpload,
  STAGED_FILE_STATUS,
  StagedFileStatus,
} from "./stagedUploadsStore";

type FileHandle = {
  id: number;
  /** Move to scanning; sets scanId */
  setScanning: (scanId: number) => void;
  /** Mark upload success; */
  setSuccess: () => void;
  /** Mark failed; */
  setFailed: () => void;
  /** Set status of file */
  setStatus: (status: StagedFileStatus) => void;
  /** Remove the file from the bucket */
  remove: () => void;
};

const EMPTY: ReadonlyArray<StagedFile> = Object.freeze([]);

// Pure selectors that DON'T allocate new arrays unnecessarily
function selectHasAny(s: StagedUpload[]) {
  for (let i = 0; i < s.length; i++) {
    if (s[i].files.length > 0) return true;
  }
  return false;
}

export function useStagedUpload(requirement: string | undefined | null = null) {
  // Memoize the selector so we don't resubscribe each render
  const selectFilesForReq = useMemo(
    () => (s: StagedUpload[]) => {
      const bucket = s.find(x => x.requirement === requirement);
      // IMPORTANT: return the original array reference if present,
      // otherwise return a shared EMPTY reference (not a new [])
      return (bucket?.files ?? EMPTY) as StagedFile[];
    },
    [requirement]
  );

  const stagedFiles = useStagedUploadsStore(selectFilesForReq);
  const hasStagedUploads = useStagedUploadsStore(selectHasAny);

  // Stable, requirement-scoped actions
  const addFile = useCallback(
    (file: StagedFileInput): FileHandle => {
      const id = stagedUploadsActions.addFile(requirement, file);
      return {
        id,
        setScanning: (scanId) =>
          stagedUploadsActions.updateById(requirement, id, {
            status: STAGED_FILE_STATUS.SCANNING,
            scanId,
          }),
        setSuccess: () =>
          stagedUploadsActions.updateById(requirement, id, {
            status: STAGED_FILE_STATUS.SUCCESS,
          }),
        setFailed: () =>
          stagedUploadsActions.updateById(requirement, id, {
            status: STAGED_FILE_STATUS.FAILED,
          }),
        setStatus: (status: StagedFileStatus) =>
          stagedUploadsActions.updateById(requirement, id, { status }),
        remove: () => stagedUploadsActions.removeById(requirement, id),
      };
    },
    [requirement]
  );
  const updateFile = useCallback(
    (file: StagedFile) => stagedUploadsActions.updateFile(requirement, file),
    [requirement]
  );
  const removeFile = useCallback(
    (file: StagedFile) => stagedUploadsActions.removeFile(requirement, file),
    [requirement]
  );

  return [stagedFiles, addFile, updateFile, removeFile, hasStagedUploads] as const;
}
